/**
 * Chat Panel Component
 * AI-powered chat interface for lecture Q&A
 * Playful light/dark card matching the classrooms.work design system
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import Mascot from '../shared/Mascot';
import { askQuestion, askQuestionStream, SourceSegment } from '../../api/chat';

interface Message {
  id: string;
  type: 'user' | 'ai';
  content: string;
  sources?: SourceSegment[];
  timestamp: Date;
  isStreaming?: boolean;
}

interface ChatPanelProps {
  lectureName: string;
  courseId: string;
  onTimestampClick: (time: number) => void;
}

const SUGGESTION_CHIPS = [
  'Summarize this lecture',
  'What are the key concepts?',
  'Explain the main topics',
];

const ChatPanel: React.FC<ChatPanelProps> = ({ lectureName, courseId, onTimestampClick }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => String(Date.now()));
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [input]);

  // Handle send message (supports passing a message string for suggestion chips)
  const handleSend = useCallback(async (messageText?: string) => {
    const question = (messageText || input).trim();
    if (!question || loading) return;

    const userMessageId = String(Date.now());
    const aiMessageId = String(Date.now() + 1);

    // Add user message
    const userMessage: Message = {
      id: userMessageId,
      type: 'user',
      content: question,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    // Add empty AI message for streaming
    const aiMessage: Message = {
      id: aiMessageId,
      type: 'ai',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    };
    setMessages((prev) => [...prev, aiMessage]);

    try {
      // Try streaming first
      await askQuestionStream(
        { question, lecture_name: lectureName, course_id: courseId, session_id: sessionId },
        {
          onSources: (data) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === aiMessageId ? { ...m, sources: data.sources } : m
              )
            );
          },
          onToken: (token) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === aiMessageId ? { ...m, content: m.content + token } : m
              )
            );
          },
          onDone: () => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === aiMessageId ? { ...m, isStreaming: false } : m
              )
            );
          },
          onError: (error) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === aiMessageId
                  ? { ...m, content: `Sorry, an error occurred: ${error}`, isStreaming: false }
                  : m
              )
            );
          },
        }
      );
      // Mark done in case onDone wasn't called (stream ended cleanly)
      setMessages((prev) =>
        prev.map((m) =>
          m.id === aiMessageId ? { ...m, isStreaming: false } : m
        )
      );
    } catch {
      // Fallback to non-streaming
      try {
        // Remove the empty streaming message
        setMessages((prev) => prev.filter((m) => m.id !== aiMessageId));

        const response = await askQuestion({
          question,
          lecture_name: lectureName,
          course_id: courseId,
          session_id: sessionId,
        });

        const fallbackMessage: Message = {
          id: String(Date.now()),
          type: 'ai',
          content: response.answer,
          sources: response.sources,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, fallbackMessage]);
      } catch (error: any) {
        setMessages((prev) => prev.filter((m) => m.id !== aiMessageId));
        const errorMessage: Message = {
          id: String(Date.now()),
          type: 'ai',
          content: `Sorry, I encountered an error: ${error.response?.data?.detail || error.message}`,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
    } finally {
      setLoading(false);
    }
  }, [input, loading, courseId, sessionId]);

  // Handle Enter key
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-gray-100 dark:border-secondary-700">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8-1.084 0-2.123-.17-3.084-.482L4 21l1.395-3.72C4.512 15.983 4 14.05 4 12c0-4.418 4.03-8 9-8s8 3.582 8 8z" />
          </svg>
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-extrabold tracking-tight text-gray-900 dark:text-white">AI Tutor</h3>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Ask questions about the lecture</p>
        </div>
        <span className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          Ready
        </span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="flex flex-col items-center text-center py-8">
            <Mascot name="book" chip="#fbbf24" className="h-16 w-16 shadow-medium animate-float-slow" blinkDelay={0.3} />
            <p className="mt-4 font-bold text-gray-900 dark:text-white">Ask me anything about the lecture!</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5 mb-5">I'll provide answers with video timestamps.</p>

            {/* Suggestion Chips */}
            <div className="flex flex-wrap justify-center gap-2">
              {SUGGESTION_CHIPS.map((chip) => (
                <button
                  key={chip}
                  onClick={() => handleSend(chip)}
                  className="px-3.5 py-1.5 text-xs font-semibold rounded-full border-2 border-gray-200 text-gray-600 hover:border-primary-500 hover:bg-primary-50 hover:text-primary-700 dark:border-secondary-600 dark:text-gray-300 dark:hover:border-primary-500 dark:hover:bg-primary-900/20 dark:hover:text-primary-300 transition-all"
                >
                  {chip}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            onTimestampClick={onTimestampClick}
          />
        ))}

        {/* Loading indicator (only when no streaming message exists) */}
        {loading && !messages.some((m) => m.isStreaming) && (
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <span className="flex w-8 h-8 items-center justify-center rounded-lg bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8-1.084 0-2.123-.17-3.084-.482L4 21l1.395-3.72C4.512 15.983 4 14.05 4 12c0-4.418 4.03-8 9-8s8 3.582 8 8z" />
                </svg>
              </span>
            </div>
            <div className="flex-1 bg-gray-100 dark:bg-secondary-700 rounded-2xl rounded-tl-sm p-3">
              <div className="flex space-x-2">
                <div className="w-2 h-2 bg-gray-400 dark:bg-secondary-500 rounded-full animate-bounce" />
                <div className="w-2 h-2 bg-gray-400 dark:bg-secondary-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                <div className="w-2 h-2 bg-gray-400 dark:bg-secondary-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Refined Input */}
      <div className="p-3 border-t border-gray-100 dark:border-secondary-700">
        <div className="flex items-end gap-2 rounded-3xl border-2 border-gray-200 bg-white focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/30 dark:border-secondary-600 dark:bg-secondary-700 transition-all px-3.5 py-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask the tutor anything..."
            className="flex-1 bg-transparent text-gray-900 placeholder-gray-400 dark:text-white dark:placeholder-secondary-400 resize-none focus:outline-none text-sm leading-relaxed max-h-[120px]"
            rows={1}
            disabled={loading}
          />
          <button
            onClick={() => handleSend()}
            disabled={!input.trim() || loading}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary-600 text-gray-900 shadow-[0_3px_0_0_rgba(0,0,0,0.12)] hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

// Message Bubble Component
interface MessageBubbleProps {
  message: Message;
  onTimestampClick: (time: number) => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, onTimestampClick }) => {
  const isUser = message.type === 'user';

  return (
    <div className={`flex items-start space-x-3 ${isUser ? 'justify-end' : ''}`}>
      {!isUser && (
        <div className="flex-shrink-0">
          <span className="flex w-8 h-8 items-center justify-center rounded-lg bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8-1.084 0-2.123-.17-3.084-.482L4 21l1.395-3.72C4.512 15.983 4 14.05 4 12c0-4.418 4.03-8 9-8s8 3.582 8 8z" />
            </svg>
          </span>
        </div>
      )}

      <div className={`flex-1 max-w-[80%] ${isUser ? 'bg-primary-600 text-gray-900 rounded-2xl rounded-tr-sm p-3' : 'bg-gray-100 text-gray-800 dark:bg-secondary-700 dark:text-gray-200 rounded-2xl rounded-tl-sm p-3'}`}>
        <div className={`prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:my-2 ${isUser ? '' : 'dark:prose-invert prose-headings:text-gray-800 dark:prose-headings:text-gray-200'}`}>
          {isUser ? (
            <p className="whitespace-pre-wrap font-medium text-gray-900">{message.content}</p>
          ) : (
            <ReactMarkdown>{message.content}</ReactMarkdown>
          )}
          {message.isStreaming && (
            <span className="inline-block w-1.5 h-4 ml-0.5 bg-primary-500 animate-blink align-text-bottom"></span>
          )}
        </div>

        {/* Sources with clickable timestamps and transcript preview */}
        {!isUser && message.sources && message.sources.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-secondary-600/50 space-y-2">
            <p className="text-xs font-bold text-gray-500 dark:text-secondary-400 uppercase tracking-wide">Sources</p>
            {message.sources.map((source, idx) => (
              <button
                key={idx}
                onClick={() => onTimestampClick(source.start_time)}
                className="w-full text-left p-2.5 bg-white dark:bg-secondary-800/80 rounded-2xl border border-gray-200 dark:border-secondary-600/40 hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-secondary-900 transition-all group"
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center space-x-2">
                    <svg className="w-3.5 h-3.5 text-gray-400 dark:text-secondary-500 group-hover:text-primary-700 dark:group-hover:text-primary-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm font-bold text-gray-700 dark:text-gray-300 group-hover:text-primary-700 dark:group-hover:text-primary-400 transition-colors">{source.timestamp_str}</span>
                  </div>
                  <span className="text-[11px] text-gray-500 dark:text-secondary-500 font-mono">{(source.score * 100).toFixed(0)}%</span>
                </div>
                {/* Transcript snippet */}
                {source.transcript_text && (
                  <p className="text-xs text-gray-500 dark:text-secondary-400 line-clamp-2 mt-1 leading-relaxed">
                    {source.transcript_text.length > 120
                      ? source.transcript_text.slice(0, 120) + '...'
                      : source.transcript_text}
                  </p>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {isUser && (
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-gray-100 dark:bg-secondary-600 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-gray-500 dark:text-secondary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPanel;
